import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const MarkdownContent = ({ content, isUser }) => {
  const renderers = {
    p: ({ children, ...props }) => (
      <p {...props} className={`leading-relaxed ${isUser ? "text-white" : "text-slate-800"} my-2`}>
        {children}
      </p>
    ),
    strong: ({ children, ...props }) => (
      <strong {...props} className={`font-semibold ${isUser ? "text-white" : "text-slate-900"}`}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em {...props} className={`italic ${isUser ? "text-white/90" : "text-slate-700"}`}>
        {children}
      </em>
    ),
    h1: ({ children, ...props }) => (
      <h1 {...props} className={`text-xl font-bold mt-4 mb-2 ${isUser ? "text-white" : "text-slate-900"}`}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 {...props} className={`text-lg font-bold mt-3 mb-1.5 ${isUser ? "text-white" : "text-slate-900"}`}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 {...props} className={`text-base font-semibold mt-2.5 mb-1 ${isUser ? "text-white" : "text-slate-800"}`}>
        {children}
      </h3>
    ),
    ul: ({ children, ...props }) => (
      <ul {...props} className={`list-disc list-inside space-y-1.5 ml-2 ${isUser ? "text-white/90" : "text-slate-700"}`}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol {...props} className={`list-decimal list-inside space-y-1.5 ml-2 ${isUser ? "text-white/90" : "text-slate-700"}`}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li {...props} className="leading-relaxed">{children}</li>
    ),
    blockquote: ({ children, ...props }) => (
      <blockquote
        {...props}
        className={`border-l-3 ${isUser ? "border-amber-300 bg-amber-500/10" : "border-orange-400 bg-orange-50"} pl-3 py-1 my-2 italic ${
          isUser ? "text-white/90" : "text-slate-700"
        }`}
      >
        {children}
      </blockquote>
    ),
    code: ({ children, ...props }) => {
      const isInline = !props.className || !props.className.startsWith("language-");
      if (isInline) {
        return (
          <code
            {...props}
            className={`px-1.5 py-0.5 rounded text-xs font-mono ${
              isUser ? "bg-white/20 text-white" : "bg-slate-100 text-slate-900"
            } ${props.className || ""}`}
          >
            {children}
          </code>
        );
      }
      return null;
    },
    pre: ({ children, ...props }) => {
      // react-markdown places the language class on the <code> child, not on <pre>
      const codeChild = React.Children.toArray(children).find(
        (child) => React.isValidElement(child) && child.type === 'code'
      );
      const language = codeChild?.props?.className?.replace('language-', '') || 'text';
      return (
        <div className="relative my-3 overflow-x-auto rounded-xl bg-slate-900 shadow-lg">
          <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 border-b border-slate-700">
            <span className="text-xs text-slate-400 font-mono">{language}</span>
            <button
              className="p-1 text-slate-400 hover:text-white transition-colors"
              onClick={() => {
                const codeText = typeof children === "string" ? children : (codeChild?.props?.children || "");
                if (codeText) navigator.clipboard.writeText(String(codeText));
              }}
              aria-label="Copy code"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 012-2h10a2 2 0 012 2v12a2 2 0 01-2 2h-10a2 2 0 01-2-2v-1" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7v5a2 2 0 01-2 2h-6a2 2 0 01-2-2v-5" />
              </svg>
            </button>
          </div>
          <pre
            {...props}
            className={`m-0 p-4 text-sm font-mono leading-relaxed ${isUser ? "text-white" : "text-slate-100"}`}
          >
            {children}
          </pre>
        </div>
      );
    },
    table: ({ children, ...props }) => (
      <div className={`overflow-x-auto my-3 rounded-lg border ${isUser ? "border-white/20" : "border-slate-200"}`}>
        <table {...props} className="w-full text-sm border-collapse">
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead {...props} className={`${isUser ? "bg-white/10 text-white" : "bg-slate-50 text-slate-900"}`}>
        {children}
      </thead>
    ),
    tbody: ({ children, ...props }) => (
      <tbody {...props} className={`${isUser ? "text-white/90" : "text-slate-700"}`}>
        {children}
      </tbody>
    ),
    tr: ({ children, ...props }) => (
      <tr {...props} className={`border-t ${isUser ? "border-white/10" : "border-slate-100"}`}>
        {children}
      </tr>
    ),
    th: ({ children, ...props }) => (
      <th {...props} className={`px-3 py-2 text-left font-semibold border-r ${isUser ? "border-white/10" : "border-slate-200"}`}>{children}</th>
    ),
    td: ({ children, ...props }) => (
      <td {...props} className={`px-3 py-2 border-r ${isUser ? "border-white/10" : "border-slate-200"}`}>{children}</td>
    ),
    hr: () => <hr className={`my-3 border-t ${isUser ? "border-white/20" : "border-slate-200"}`} />,
    a: ({ href, children, ...props }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline hover:no-underline transition-colors ${isUser ? "text-amber-200 hover:text-amber-100" : "text-orange-600 hover:text-orange-700"}`}
        {...props}
      >
        {children}
      </a>
    ),
  };

  return (
    <div className={`prose prose-sm max-w-full select-text break-words ${isUser ? "prose-invert" : ""}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={renderers}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

const SourcesSection = ({ sources }) => {
  if (!sources || sources.length === 0) return null;

  return (
    <details className="mt-3 group">
      <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700 select-none">
        <svg className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <span>Sources ({sources.length})</span>
      </summary>
      <ul className="mt-2 space-y-2 pl-6 border-l border-slate-200">
        {sources.map((src, i) => (
          <li key={i} className="relative pl-2 before:content-[''] before:absolute before:left-[-6px] before:top-[6px] before:w-1.5 before:h-1.5 before:rounded-full before:bg-slate-300">
            <a
              href={src.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-slate-600 hover:text-orange-600 transition-colors group"
            >
              <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-orange-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l4-4a4 4 0 000-5.656z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10a2 2 0 012 2v2a2 2 0 01-2 2H9a2 2 0 01-2-2v-2a2 2 0 012-2h6z" />
              </svg>
              <span className="truncate max-w-[200px]">{src.title}</span>
              <span className="text-[10px] text-slate-400 hidden sm:inline">→</span>
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
};

const FollowUpChips = ({ followUps, onSelect }) => {
  if (!followUps || followUps.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-1.5 animate-fade-in" style={{ overflow: 'hidden' }}>
      {followUps.map((fu, i) => (
        <button
          key={i}
          onClick={() => onSelect(fu)}
          className="text-[11px] font-medium px-3 py-1.5 rounded-full border border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100 hover:border-orange-400 hover:shadow-sm transition-all active:scale-95"
        >
          {fu}
        </button>
      ))}
    </div>
  );
};

export function MessageContent({ message, onSelectFollowUp }) {
  const { content, isUser, sources, followUps, isError, files } = message;

  return (
    <div className={`w-full px-4 py-2.5 rounded-2xl ${isUser ? "text-right" : ""}`}>
      <div
        className={`inline-block max-w-[85%] px-4 py-2.5 rounded-2xl ${
          isUser
            ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-tr-md shadow-lg shadow-orange-500/25"
            : isError
            ? "bg-red-50 text-red-700 border border-red-200 rounded-tl-md shadow-sm"
            : "bg-slate-50 text-slate-900 rounded-tl-md shadow-sm"
        }`}
      >
        <MarkdownContent content={content} isUser={isUser} />

        {files && files.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white/30 rounded-lg text-xs">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {f.name} ({(f.size / 1024).toFixed(0)}KB)
              </span>
            ))}
          </div>
        )}

        <SourcesSection sources={sources} />
      </div>

      {!isUser && !isError && followUps && (
        <FollowUpChips followUps={followUps} onSelect={onSelectFollowUp} />
      )}
    </div>
  );
}

export default MessageContent;